import { z } from 'zod'
import { isValidTimeZone } from '../recurrence.ts'

/**
 * Bringing a church across from Planning Center — the parts every product's
 * import shares: which products there are and in what order they run, how a
 * run reports its progress, and the conversions every product needs (time
 * zones, dates in the church's zone, HTML to text).
 *
 * Each product's own mapping lives beside this file (`people.ts`,
 * `services.ts`…), pure and unit-tested; the API's importers fetch, call
 * these, and write.
 */

/** In the order a run takes them: everything after People finds its people through it. */
export const PCO_PRODUCTS = [
  'people',
  'services',
  'groups',
  'check_ins',
  'calendar',
  'registrations',
  'giving',
] as const
export type PcoProduct = (typeof PCO_PRODUCTS)[number]

export const PCO_PRODUCT_LABELS: Record<PcoProduct, string> = {
  people: 'People',
  services: 'Services',
  groups: 'Groups',
  check_ins: 'Check-Ins',
  calendar: 'Calendar',
  registrations: 'Registrations',
  giving: 'Giving',
}

/** The API path prefix of each product (`https://api.planningcenteronline.com/<path>/v2`). */
export const PCO_PRODUCT_PATHS: Record<PcoProduct, string> = {
  people: 'people',
  services: 'services',
  groups: 'groups',
  check_ins: 'check-ins',
  calendar: 'calendar',
  registrations: 'registrations',
  giving: 'giving',
}

/**
 * The API version each product is read at (sent as `X-PCO-API-Version`).
 * Pinned: these are what the import was written and checked against, and a
 * newer version can rename what the mapping reads.
 */
export const PCO_API_VERSIONS: Record<PcoProduct, string> = {
  people: '2026-10-05',
  services: '2018-11-01',
  groups: '2023-07-10',
  check_ins: '2025-05-28',
  calendar: '2026-06-22',
  registrations: '2025-05-01',
  giving: '2019-10-18',
}

/** Products a run may not start without a permission beyond `org:admin`. */
export const PCO_PRODUCT_PERMISSIONS: Partial<Record<PcoProduct, string>> = {
  giving: 'giving:admin',
}

export const PCO_IMPORT_STATUSES = [
  'queued',
  'running',
  'cancelling',
  'cancelled',
  'failed',
  'completed',
] as const
export type PcoImportStatus = (typeof PCO_IMPORT_STATUSES)[number]

/** Still being worked on (or about to be): the screen polls, and no second run may start. */
export const isActivePcoImport = (status: string) =>
  status === 'queued' || status === 'running' || status === 'cancelling'

export const PCO_ISSUE_SEVERITIES = ['info', 'warning', 'error', 'review'] as const
export type PcoIssueSeverity = (typeof PCO_ISSUE_SEVERITIES)[number]

export const PCO_STEP_STATUSES = ['pending', 'running', 'done', 'failed', 'skipped'] as const
export type PcoStepStatus = (typeof PCO_STEP_STATUSES)[number]

/**
 * One step of a run ("Services · Plans"): what it has done so far, and where
 * to pick up. Written after every page, so it is both the progress bar and
 * the resume point.
 */
export interface PcoStepProgress {
  product: PcoProduct
  /** Unique within the product: `people`, `households`, `plans`… */
  key: string
  label: string
  status: PcoStepStatus
  /** How many records Planning Center says there are, once known. */
  total: number | null
  /** Records read from Planning Center so far. */
  fetched: number
  created: number
  updated: number
  /** Unchanged since the last run, or deliberately left out (with an issue saying why). */
  skipped: number
  failed: number
  /** Where a resumed step starts: the step's own bookmark (usually the next page's URL). */
  cursor: string | null
  startedAt: string | null
  finishedAt: string | null
}

export const PCO_FILE_CHOICES = ['none', 'songs', 'songs_and_upcoming', 'all'] as const
export type PcoFileChoice = (typeof PCO_FILE_CHOICES)[number]

export const PCO_FILE_CHOICE_LABELS: Record<PcoFileChoice, string> = {
  none: 'No files',
  songs: 'Song and arrangement files',
  songs_and_upcoming: 'Song files and upcoming plans’ files',
  all: 'Every file, past plans included',
}

/** What a run was asked to do beyond its products. */
export interface PcoImportOptions {
  files: PcoFileChoice
  /**
   * How far back history goes (`YYYY-MM-DD`, the church's local date): past
   * plans, check-ins, gifts, meetings, calendar dates and notes from before it
   * stay behind. Null: everything. What's ahead always comes.
   */
  since: string | null
  /** Steps left out (`product.key`), as chosen when the run started. */
  skip: string[]
}

export const DEFAULT_PCO_IMPORT_OPTIONS: PcoImportOptions = {
  files: 'songs_and_upcoming',
  since: null,
  skip: [],
}

/** One step as the start form shows it, with what it needs. */
export interface PcoStepInfo {
  product: PcoProduct
  key: string
  label: string
  /** Can't be left out when its product is chosen (everything else in it hangs off it). */
  required: boolean
  /** Steps (`product.key`) it can't run without. */
  requires: string[]
  /** Honours "how far back". */
  history: boolean
  /**
   * Links its records to people. It runs only once People is here (this run,
   * or an earlier one): a link to someone not yet here would be left out, and
   * a later run would skip the record as unchanged and never add it.
   */
  people: boolean
  /** Steps that add to this one when they run too ("who served" from Teams). */
  fuller: { step: string; adds: string }[]
}

/** The People step every step that links to people waits for. */
export const PCO_PEOPLE_STEP = 'people.people'

export const pcoStepId = (step: { product: string; key: string }) => `${step.product}.${step.key}`

/**
 * What a run will do, from the products chosen and the steps left out: the
 * steps left out on purpose, then every step that needs one of them (and so
 * on), and every step that links to people when People isn't coming this run
 * and hasn't come before (`peopleImported`). Required steps can't be left
 * out. Pure, so the start form shows the same answer the server will act on.
 */
export function pcoResolveSteps(
  catalog: readonly PcoStepInfo[],
  products: readonly PcoProduct[],
  skip: readonly string[],
  peopleImported = false,
): {
  steps: PcoStepInfo[]
  /** Left out because a step they need is out: id → the id it needed. */
  dropped: Map<string, string>
  /** Asked to leave out a step that can't be. */
  refused: string[]
} {
  const chosen = catalog.filter((step) => products.includes(step.product))
  const skipped = new Set(skip)
  const refused = chosen.filter((s) => s.required && skipped.has(pcoStepId(s))).map(pcoStepId)
  const included = new Set(
    chosen.filter((s) => s.required || !skipped.has(pcoStepId(s))).map(pcoStepId),
  )
  const dropped = new Map<string, string>()
  // Until nothing changes: a step whose need is out goes out too.
  let changed = true
  while (changed) {
    changed = false
    for (const step of chosen) {
      const id = pcoStepId(step)
      if (!included.has(id)) continue
      // A need in a product not chosen this run counts as met (it came in an
      // earlier run, or the caller judges it); People is judged here.
      const missing =
        step.requires.find(
          (need) => !included.has(need) && chosen.some((s) => pcoStepId(s) === need),
        ) ??
        (step.people && !peopleImported && !included.has(PCO_PEOPLE_STEP)
          ? PCO_PEOPLE_STEP
          : undefined)
      if (missing) {
        included.delete(id)
        dropped.set(id, missing)
        changed = true
      }
    }
  }
  return { steps: chosen.filter((s) => included.has(pcoStepId(s))), dropped, refused }
}

/**
 * Whether a record from `when` is older than "how far back". `when` is the
 * church's local date (`YYYY-MM-DD`) or an instant (converted in `timeZone`).
 * No cut-off, or no date to judge by: it comes.
 */
export function pcoBeforeSince(
  when: string | Date | null | undefined,
  since: string | null,
  timeZone: string,
): boolean {
  if (!since || !when) return false
  const local =
    typeof when === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(when)
      ? when
      : pcoLocalDate(when, timeZone)
  return local < since
}

/** The start form's "how far back" choices, from a local date (`YYYY-MM-DD`). */
export function pcoSinceChoices(today: string): { label: string; value: string | null }[] {
  const yearsBack = (n: number) => `${Number(today.slice(0, 4)) - n}${today.slice(4)}`
  return [
    { label: 'All history', value: null },
    { label: 'The last year', value: yearsBack(1) },
    { label: 'The last 2 years', value: yearsBack(2) },
    { label: 'The last 5 years', value: yearsBack(5) },
  ]
}

/**
 * A personal access token, made at api.planningcenteronline.com/oauth/applications
 * ("Personal Access Tokens"): an application id and a secret.
 */
export const pcoConnectSchema = z.object({
  applicationId: z.string().trim().min(10, 'Paste the application ID').max(200),
  secret: z.string().trim().min(10, 'Paste the secret').max(200),
})
export type PcoConnectInput = z.infer<typeof pcoConnectSchema>

export const pcoStartImportSchema = z.object({
  products: z
    .array(z.enum(PCO_PRODUCTS))
    .min(1, 'Choose at least one product')
    .transform((products) => PCO_PRODUCTS.filter((product) => products.includes(product))),
  files: z.enum(PCO_FILE_CHOICES).default(DEFAULT_PCO_IMPORT_OPTIONS.files),
  since: z.iso
    .date('Choose a date')
    .nullish()
    .transform((v) => v ?? null),
  skip: z.array(z.string().max(80)).max(100).default([]),
})
export type PcoStartImportInput = z.input<typeof pcoStartImportSchema>

/** A step that hasn't run yet. */
export function pendingPcoStep(product: PcoProduct, key: string, label: string): PcoStepProgress {
  return {
    product,
    key,
    label,
    status: 'pending',
    total: null,
    fetched: 0,
    created: 0,
    updated: 0,
    skipped: 0,
    failed: 0,
    cursor: null,
    startedAt: null,
    finishedAt: null,
  }
}

/** A product's share of a run, for its row on the screen. */
export interface PcoProductSummary {
  product: PcoProduct
  status: PcoStepStatus
  done: number
  steps: number
  fetched: number
  created: number
  updated: number
  skipped: number
  failed: number
}

export function summarisePcoSteps(steps: PcoStepProgress[]): PcoProductSummary[] {
  const products = PCO_PRODUCTS.filter((product) => steps.some((s) => s.product === product))
  return products.map((product) => {
    const own = steps.filter((step) => step.product === product)
    const sum = (field: 'fetched' | 'created' | 'updated' | 'skipped' | 'failed') =>
      own.reduce((total, step) => total + step[field], 0)
    const status: PcoStepStatus = own.some((s) => s.status === 'failed')
      ? 'failed'
      : own.some((s) => s.status === 'running')
        ? 'running'
        : own.every((s) => s.status === 'done' || s.status === 'skipped')
          ? 'done'
          : own.some((s) => s.status === 'done')
            ? 'running'
            : 'pending'
    return {
      product,
      status,
      done: own.filter((s) => s.status === 'done' || s.status === 'skipped').length,
      steps: own.length,
      fetched: sum('fetched'),
      created: sum('created'),
      updated: sum('updated'),
      skipped: sum('skipped'),
      failed: sum('failed'),
    }
  })
}

// ---- time ---------------------------------------------------------------------

/**
 * Rails time zone names (what Planning Center may give for an organisation or
 * a campus) → IANA. ActiveSupport's own mapping, so every name it can send
 * has an answer.
 */
const RAILS_TIME_ZONES: Record<string, string> = {
  'International Date Line West': 'Etc/GMT+12',
  'Midway Island': 'Pacific/Midway',
  'American Samoa': 'Pacific/Pago_Pago',
  Hawaii: 'Pacific/Honolulu',
  Alaska: 'America/Juneau',
  'Pacific Time (US & Canada)': 'America/Los_Angeles',
  Tijuana: 'America/Tijuana',
  'Mountain Time (US & Canada)': 'America/Denver',
  Arizona: 'America/Phoenix',
  Chihuahua: 'America/Chihuahua',
  Mazatlan: 'America/Mazatlan',
  'Central Time (US & Canada)': 'America/Chicago',
  Saskatchewan: 'America/Regina',
  Guadalajara: 'America/Mexico_City',
  'Mexico City': 'America/Mexico_City',
  Monterrey: 'America/Monterrey',
  'Central America': 'America/Guatemala',
  'Eastern Time (US & Canada)': 'America/New_York',
  'Indiana (East)': 'America/Indiana/Indianapolis',
  Bogota: 'America/Bogota',
  Lima: 'America/Lima',
  Quito: 'America/Lima',
  'Atlantic Time (Canada)': 'America/Halifax',
  Caracas: 'America/Caracas',
  'La Paz': 'America/La_Paz',
  Santiago: 'America/Santiago',
  Newfoundland: 'America/St_Johns',
  Brasilia: 'America/Sao_Paulo',
  'Buenos Aires': 'America/Argentina/Buenos_Aires',
  Montevideo: 'America/Montevideo',
  Georgetown: 'America/Guyana',
  'Puerto Rico': 'America/Puerto_Rico',
  Greenland: 'America/Godthab',
  'Mid-Atlantic': 'Atlantic/South_Georgia',
  Azores: 'Atlantic/Azores',
  'Cape Verde Is.': 'Atlantic/Cape_Verde',
  Dublin: 'Europe/Dublin',
  Edinburgh: 'Europe/London',
  Lisbon: 'Europe/Lisbon',
  London: 'Europe/London',
  Casablanca: 'Africa/Casablanca',
  Monrovia: 'Africa/Monrovia',
  UTC: 'Etc/UTC',
  Belgrade: 'Europe/Belgrade',
  Bratislava: 'Europe/Bratislava',
  Budapest: 'Europe/Budapest',
  Ljubljana: 'Europe/Ljubljana',
  Prague: 'Europe/Prague',
  Sarajevo: 'Europe/Sarajevo',
  Skopje: 'Europe/Skopje',
  Warsaw: 'Europe/Warsaw',
  Zagreb: 'Europe/Zagreb',
  Brussels: 'Europe/Brussels',
  Copenhagen: 'Europe/Copenhagen',
  Madrid: 'Europe/Madrid',
  Paris: 'Europe/Paris',
  Amsterdam: 'Europe/Amsterdam',
  Berlin: 'Europe/Berlin',
  Bern: 'Europe/Zurich',
  Zurich: 'Europe/Zurich',
  Rome: 'Europe/Rome',
  Stockholm: 'Europe/Stockholm',
  Vienna: 'Europe/Vienna',
  'West Central Africa': 'Africa/Algiers',
  Bucharest: 'Europe/Bucharest',
  Cairo: 'Africa/Cairo',
  Helsinki: 'Europe/Helsinki',
  Kyiv: 'Europe/Kiev',
  Riga: 'Europe/Riga',
  Sofia: 'Europe/Sofia',
  Tallinn: 'Europe/Tallinn',
  Vilnius: 'Europe/Vilnius',
  Athens: 'Europe/Athens',
  Istanbul: 'Europe/Istanbul',
  Minsk: 'Europe/Minsk',
  Jerusalem: 'Asia/Jerusalem',
  Harare: 'Africa/Harare',
  Pretoria: 'Africa/Johannesburg',
  Kaliningrad: 'Europe/Kaliningrad',
  Moscow: 'Europe/Moscow',
  'St. Petersburg': 'Europe/Moscow',
  Volgograd: 'Europe/Volgograd',
  Samara: 'Europe/Samara',
  Kuwait: 'Asia/Kuwait',
  Riyadh: 'Asia/Riyadh',
  Nairobi: 'Africa/Nairobi',
  Baghdad: 'Asia/Baghdad',
  Tehran: 'Asia/Tehran',
  'Abu Dhabi': 'Asia/Muscat',
  Muscat: 'Asia/Muscat',
  Baku: 'Asia/Baku',
  Tbilisi: 'Asia/Tbilisi',
  Yerevan: 'Asia/Yerevan',
  Kabul: 'Asia/Kabul',
  Ekaterinburg: 'Asia/Yekaterinburg',
  Islamabad: 'Asia/Karachi',
  Karachi: 'Asia/Karachi',
  Tashkent: 'Asia/Tashkent',
  Chennai: 'Asia/Kolkata',
  Kolkata: 'Asia/Kolkata',
  Mumbai: 'Asia/Kolkata',
  'New Delhi': 'Asia/Kolkata',
  Kathmandu: 'Asia/Kathmandu',
  Astana: 'Asia/Dhaka',
  Dhaka: 'Asia/Dhaka',
  'Sri Jayawardenepura': 'Asia/Colombo',
  Almaty: 'Asia/Almaty',
  Novosibirsk: 'Asia/Novosibirsk',
  Rangoon: 'Asia/Rangoon',
  Bangkok: 'Asia/Bangkok',
  Hanoi: 'Asia/Bangkok',
  Jakarta: 'Asia/Jakarta',
  Krasnoyarsk: 'Asia/Krasnoyarsk',
  Beijing: 'Asia/Shanghai',
  Chongqing: 'Asia/Chongqing',
  'Hong Kong': 'Asia/Hong_Kong',
  Urumqi: 'Asia/Urumqi',
  'Kuala Lumpur': 'Asia/Kuala_Lumpur',
  Singapore: 'Asia/Singapore',
  Taipei: 'Asia/Taipei',
  Perth: 'Australia/Perth',
  Irkutsk: 'Asia/Irkutsk',
  Ulaanbaatar: 'Asia/Ulaanbaatar',
  Seoul: 'Asia/Seoul',
  Osaka: 'Asia/Tokyo',
  Sapporo: 'Asia/Tokyo',
  Tokyo: 'Asia/Tokyo',
  Yakutsk: 'Asia/Yakutsk',
  Darwin: 'Australia/Darwin',
  Adelaide: 'Australia/Adelaide',
  Canberra: 'Australia/Melbourne',
  Melbourne: 'Australia/Melbourne',
  Sydney: 'Australia/Sydney',
  Brisbane: 'Australia/Brisbane',
  Hobart: 'Australia/Hobart',
  Vladivostok: 'Asia/Vladivostok',
  Guam: 'Pacific/Guam',
  'Port Moresby': 'Pacific/Port_Moresby',
  Magadan: 'Asia/Magadan',
  Srednekolymsk: 'Asia/Srednekolymsk',
  'Solomon Is.': 'Pacific/Guadalcanal',
  'New Caledonia': 'Pacific/Noumea',
  Fiji: 'Pacific/Fiji',
  Kamchatka: 'Asia/Kamchatka',
  'Marshall Is.': 'Pacific/Majuro',
  Auckland: 'Pacific/Auckland',
  Wellington: 'Pacific/Auckland',
  "Nuku'alofa": 'Pacific/Tongatapu',
  'Tokelau Is.': 'Pacific/Fakaofo',
  'Chatham Is.': 'Pacific/Chatham',
  Samoa: 'Pacific/Apia',
}

/**
 * Planning Center's zone name → an IANA one we can compute with. Already IANA
 * passes through; a Rails name is translated; anything else is null (the
 * caller falls back to the church's own zone and says so).
 */
export function pcoTimeZoneToIana(name: string | null | undefined): string | null {
  if (!name) return null
  const trimmed = name.trim()
  const mapped = RAILS_TIME_ZONES[trimmed]
  if (mapped) return mapped
  return isValidTimeZone(trimmed) ? trimmed : null
}

/** The wall-clock parts of an instant in a zone. */
function zonedParts(instant: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(instant)
  const at = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '00'
  return {
    date: `${at('year')}-${at('month')}-${at('day')}`,
    time: `${at('hour') === '24' ? '00' : at('hour')}:${at('minute')}`,
  }
}

/**
 * The church's local date (`YYYY-MM-DD`) of an instant Planning Center gave
 * in UTC. Never cut the date off the UTC string: a 7 pm service in Chicago is
 * already tomorrow in UTC.
 */
export function pcoLocalDate(utc: string | Date, timeZone: string): string {
  return zonedParts(typeof utc === 'string' ? new Date(utc) : utc, timeZone).date
}

/** The church's local time (`HH:MM`) of an instant Planning Center gave in UTC. */
export function pcoLocalTime(utc: string | Date, timeZone: string): string {
  return zonedParts(typeof utc === 'string' ? new Date(utc) : utc, timeZone).time
}

// ---- text ---------------------------------------------------------------------

const ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  rsquo: '’',
  lsquo: '‘',
  rdquo: '”',
  ldquo: '“',
  mdash: '—',
  ndash: '–',
  hellip: '…',
}

/**
 * Planning Center's rich text (event descriptions, item details, notes) as
 * plain text: block ends become line breaks, list items get a dash, links keep
 * their address, entities are decoded, and runs of blank lines collapse.
 */
export function pcoHtmlToText(html: string | null | undefined): string {
  if (!html) return ''
  const text = html
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\s*li[^>]*>/gi, '\n- ')
    .replace(/<\s*\/\s*(ul|ol)\s*>/gi, '\n\n')
    .replace(/<\s*\/\s*(p|div|h[1-6]|ul|ol|blockquote|tr)\s*>/gi, '\n')
    .replace(/<a\s[^>]*href="([^"]+)"[^>]*>(.*?)<\/a>/gi, (_, href: string, label: string) =>
      label.replace(/<[^>]+>/g, '').trim() === href ? href : `${label} (${href})`,
    )
    .replace(/<[^>]+>/g, '')
    .replace(/&(#\d+|#x[0-9a-f]+|[a-z]+);/gi, (match, code: string) => {
      if (code.startsWith('#x') || code.startsWith('#X'))
        return String.fromCodePoint(Number.parseInt(code.slice(2), 16))
      if (code.startsWith('#')) return String.fromCodePoint(Number.parseInt(code.slice(1), 10))
      return ENTITIES[code.toLowerCase()] ?? match
    })
  return text
    .split('\n')
    .map((line) => line.replace(/[ \t]+/g, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/** A string, cut to `max` characters with an ellipsis if it was longer. */
export function pcoClip(value: string | null | undefined, max: number): string | null {
  if (value === null || value === undefined) return null
  const trimmed = value.trim()
  if (trimmed === '') return null
  return trimmed.length <= max ? trimmed : `${trimmed.slice(0, max - 1)}…`
}
